import React from 'react';
import { useTranslation } from 'react-i18next';
import './HouseBars.css';

/* The house standing, as bars. One module because THREE screens draw it — the member home card,
   /my-competition, and the day's board — and a standing that looks like three different things
   depending on where you opened it is three chances to read it wrong.

   ★★ THE NUMBER IS NEVER COMPUTED HERE. Every caller hands over rows that came out of
   `company_house_points` or `fetch_house_scores` on the server — the same queries the admin
   screens and the beamer board read. This component sorts nothing and adds nothing up; it only
   decides how wide to draw what it was given. The invariant is in
   shared_models/queries/house_points.py: totals read the STANDING, breakdowns read the LEDGER,
   and nothing ever sums both.

   ⚠️ THE BAR IS RELATIVE TO THE LEADER, not to a round number. A scale that ran to 2 000 would
   make a leader on 1 840 look like it was losing to something off-screen. The longest bar is
   always the house in front, which is the one fact a bar chart has to get right.

   ⚠️ Negative totals are real — points get revoked — and a negative width is a bar that draws
   backwards over its neighbour. They clamp to zero WIDTH and keep their number, so the row still
   says -40 while the bar says "nothing to show". */

const cssUrl = (image) => (
  typeof image === 'string' && image.includes(' ') ? encodeURI(image) : image);

export const DEFAULT_HOUSE_COLOR = '#5a6472';

/* The colour disc that IS the house — its picture when it has one, its colour when it does not.
   Same idiom as the admin's house stats line, so one house looks like itself on every screen. */
export const HouseSwatch = ({ house, size }) => (
  <span
    className={`hbar__swatch${size ? ` hbar__swatch--${size}` : ''}`}
    style={{
      backgroundColor: (house && house.color) || DEFAULT_HOUSE_COLOR,
      backgroundImage: house && house.image ? `url("${cssUrl(house.image)}")` : undefined,
    }}
  />
);

const HouseBars = ({ rows, mineId, max, onPick }) => {
  const { t } = useTranslation();
  const houses = rows || [];
  if (houses.length === 0) return null;

  // The leader sets the scale. `max` lets a caller pin two charts to the same scale; without it
  // each chart scales to its own leader, which is what a single chart wants.
  const top = max != null
    ? max
    : houses.reduce((best, h) => Math.max(best, Number(h.points) || 0), 0);

  return (
    <div className="hbars">
      {houses.map((house) => {
        const points = Number(house.points) || 0;
        // Clamped at both ends: never wider than the track, never backwards. A house on zero
        // still gets a sliver so the row does not read as a missing house.
        const pct = top > 0 ? Math.max(0, Math.min(100, (points / top) * 100)) : 0;
        const isMine = !!mineId && String(house.id).toLowerCase() === String(mineId).toLowerCase();
        const Tag = onPick ? 'button' : 'div';
        return (
          <Tag
            key={house.id}
            type={onPick ? 'button' : undefined}
            onClick={onPick ? () => onPick(house) : undefined}
            className={`hbar${isMine ? ' hbar--mine' : ''}${onPick ? ' hbar--btn' : ''}`}
          >
            <HouseSwatch house={house} />
            <span className="hbar__body">
              <span className="hbar__top">
                <span className="hbar__name">
                  {house.name}
                  {/* Said in words, not only in a highlight: a colour difference alone is not a
                      label, and this is the one row the reader is looking for. */}
                  {isMine && <span className="hbar__mine">{t('houseMine')}</span>}
                </span>
                <span className="hbar__n">{Number(points).toLocaleString()}</span>
              </span>
              <span className="hbar__track">
                <span
                  className="hbar__fill"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: house.color || DEFAULT_HOUSE_COLOR,
                  }}
                />
              </span>
            </span>
          </Tag>
        );
      })}
    </div>
  );
};

export default HouseBars;
